import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";

const UPDATED = "June 5, 2026";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service" },
      {
        name: "description",
        content:
          "The terms for using CoolSymbols.cool — a free Unicode symbol and font tool provided as-is. Copy and use any symbol or generated text freely; acceptable use, intellectual property, disclaimers and governing law.",
      },
      { property: "og:title", content: "Terms of Service" },
      {
        property: "og:description",
        content: "The terms for using CoolSymbols.cool — free, as-is, copy and paste anything you make.",
      },
      { property: "og:url", content: abs("/terms") },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: abs("/terms") }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-foreground/80">Terms</span>
        </nav>

        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Terms of Service</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: {UPDATED}</p>

        <div className="prose-policy mt-8 space-y-8 text-foreground/85">
          <section className="rounded-xl border border-border bg-surface/40 p-5">
            <h2 className="font-display text-lg font-semibold text-foreground">The short version</h2>
            <p className="mt-2">
              CoolSymbols.cool (“we”, “us”) is a free tool. Use it as much as you like, and copy and
              use any symbol or styled text you make — including for commercial work — with no
              attribution required. It is provided “as is”, with no warranties, and there is no
              account to manage. The points below are the full terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">1. Acceptance</h2>
            <p className="mt-2">
              By accessing or using coolsymbols.cool (the “site”), you agree to these Terms of Service
              and to our{" "}
              <Link to="/privacy" className="text-accent hover:underline">
                Privacy Policy
              </Link>
              . If you do not agree, please do not use the site.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">2. The service</h2>
            <p className="mt-2">
              The site lets you browse, search and copy Unicode symbols and characters, and convert
              plain text into styled Unicode text using the on-site generators. It is free to use and
              requires no account. We may add, change, suspend or discontinue any part of the site at
              any time without notice.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">3. Acceptable use</h2>
            <p className="mt-2">You agree not to:</p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>use the site for any unlawful purpose, or to harass, abuse, defame or harm others;</li>
              <li>
                attempt to disrupt, overload, probe or gain unauthorised access to the site, its
                infrastructure or its underlying systems;
              </li>
              <li>
                scrape, crawl or copy the site’s pages or data in bulk by automated means in a way that
                places an unreasonable load on it or replicates the service;
              </li>
              <li>
                misrepresent the site as your own, or remove or obscure any notices on it; or
              </li>
              <li>use the site in any way that violates the rights of others or any applicable law.</li>
            </ul>
            <p className="mt-3">
              We may restrict or block access if the site is used in breach of these terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">
              4. Symbols and what you create
            </h2>
            <p className="mt-2">
              The individual characters and symbols on the site are part of the public Unicode Standard
              and are not owned by us. Copying a symbol or a piece of generated text and using it
              wherever you like — in a bio, a post, a document, a logo or a commercial product — is the
              entire point of the site, and you may do so freely, with no attribution required and at
              no cost.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">
              5. Our content and intellectual property
            </h2>
            <p className="mt-2">
              Everything that makes up the site itself — its original written descriptions, its
              organisation and selection of content, its design, layout, branding and source code — is
              owned by us or our licensors and is protected by intellectual-property laws. You may not
              copy, scrape, republish or build a competing or substantially similar service from the
              site’s content or code without our written permission. This does not limit your free use
              of the Unicode symbols and generated text described in section 4.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">6. Third-party links</h2>
            <p className="mt-2">
              The site may link to third-party resources, such as the Unicode Consortium or reference
              material about a character. We do not control those sites and are not responsible for
              their content, policies or availability.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">7. No warranty</h2>
            <p className="mt-2">
              The site is provided “as is” and “as available”, without warranties of any kind, whether
              express or implied, including any implied warranties of merchantability, fitness for a
              particular purpose, accuracy or non-infringement. We do not warrant that the site will be
              uninterrupted or error-free, that every character will render identically on every device
              or platform, or that the information on it is complete or up to date.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">
              8. Limitation of liability
            </h2>
            <p className="mt-2">
              To the maximum extent permitted by law, we will not be liable for any indirect,
              incidental, special, consequential or punitive damages, or for any loss of data, profits
              or goodwill, arising out of or related to your use of (or inability to use) the site,
              even if we have been advised of the possibility of such damages. Because the site is
              provided free of charge, our total liability for any claim relating to it is limited to
              one hundred U.S. dollars (US$100). Some jurisdictions do not allow certain limitations,
              in which case the limitations apply to the fullest extent permitted.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">9. Indemnity</h2>
            <p className="mt-2">
              You agree to hold us harmless from any claims, losses or expenses arising out of your
              misuse of the site or your violation of these terms or of any law or third-party right.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">10. Changes to these terms</h2>
            <p className="mt-2">
              We may update these terms from time to time. When we do, we will revise the “last
              updated” date above, and your continued use of the site after a change takes effect means
              you accept the updated terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">11. Governing law</h2>
            <p className="mt-2">
              These terms are governed by the laws of the State of New York and the federal laws of the
              United States applicable therein, without regard to conflict-of-laws rules. You agree
              that the state and federal courts located in New York will have exclusive jurisdiction
              over any dispute arising out of or relating to these terms or the site, subject to any
              mandatory consumer-protection rights you may have under the law of your place of
              residence.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">12. Contact</h2>
            <p className="mt-2">
              Questions about these terms? Email{" "}
              <a href="mailto:privacy@coolsymbols.cool" className="text-accent hover:underline">
                privacy@coolsymbols.cool
              </a>
              .
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
